<template>
	<div class="d-flex flex-column gap-2">
		<ProfileAvatarEdit
			:class="isCreating ? 'avatar-offset' : ''"
			:picture="form.avatar"
			:hide-header="isCreating"
			@change="avatarChange"
		/>
		<ProfileCoverEdit
			v-if="!isCreating"
			:picture="form.cover"
			:is-x-s="isXS"
			@change="coverChange"
		/>
		<PForm class="d-flex flex-column">
			<ProfileName :model-value="profile.name" :error="formErrors.name" @change="nameChange" />
			<InputText
				v-if="!isCreating"
				v-model="form.email"
				:name="$t('web.widget_chat.email_form.email_label')"
				placeholder="mymail@gmail.com"
				:label="$t('web.widget_chat.email_form.email_label')"
				size="s"
				disabled
			/>
			<InputText
				:model-value="form.birthYear"
				:error="formErrors.birthYear"
				:name="$t('profileCreation_yearBirth')"
				:placeholder="$t('profileCreation_yearBirth_placeholder')"
				:label="$t('profileCreation_yearBirth')"
				size="s"
				type="number"
				@change="yearChange"
			/>
			<GoogleBrowser
				:model-value="profile.countryName"
				:types="['country']"
				:label="$t('profileCreation_whichCountry')"
				:placeholder="String($t('profileCreation_location_placeholder'))"
				:error="formErrors.country"
				size="s"
				keep-selected
				:is-x-s="isXS"
				@select="selectCountry"
			/>
			<Checkbox
				v-if="isCreating"
				:model-value="profile.consentCommercialCommunications"
				squared
				@change="toggleCommercialConsent"
			>
				<template #label>
					<label class="d-flex flex-column gap-1 text-neutral-700">
						<span class="text-body-m">
							{{ $t('profileCreation_marketingConsent_1') }}
						</span>
					</label>
				</template>
			</Checkbox>
		</PForm>
	</div>
</template>

<script setup lang="ts">
	import { ref } from 'vue'
	import { storeToRefs } from 'pinia'
	import { Profile } from '~/models/Profile'
	import { useDeviceStore } from '~/store/device'
	import { Spot2 } from '~/models/Spot'
	import { Destination2 } from '~/models/Destination'
	import InputText from '~/components/passporterUi/InputText.vue'

	const deviceStore = useDeviceStore()
	const { isXS } = storeToRefs(deviceStore)

	interface Props {
		profile: Profile
		formErrors: Partial<Record<keyof Profile, string>>
		isCreating?: boolean
	}

	const props = withDefaults(defineProps<Props>(), {
		isCreating: false,
	})
	const emit = defineEmits(['change'])

	const form = ref<Profile>({ ...props.profile })

	const avatarChange = (avatar: { url: string; id: string }) => {
		form.value.avatar = avatar.url
		form.value.avatarId = avatar.id
		emit('change', form.value)
	}

	const coverChange = (cover: { url: string; id: string }) => {
		form.value.cover = cover.url
		form.value.coverId = cover.id
		emit('change', form.value)
	}

	const nameChange = (name: string) => {
		form.value.name = name
		emit('change', form.value)
	}

	const yearChange = (inputEvent: InputEvent) => {
		form.value.birthYear = (inputEvent.target as HTMLInputElement)?.value ?? ''
		emit('change', form.value)
	}

	const selectCountry = (country: Spot2 | Destination2) => {
		form.value.country = country.id!
		form.value.countryName = country.name
		emit('change', form.value)
	}

	const toggleCommercialConsent = (event: Event) => {
		const target: HTMLInputElement = event.target! as unknown as HTMLInputElement
		const checked: boolean = target.checked
		form.value.consentCommercialCommunications = checked
		emit('change', form.value)
	}
</script>
<style lang="scss" scoped>
	.avatar-offset {
		transform: translateY(-25%);
	}
</style>
